import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchActiveCountries } from '@/shared/api/places';
import { toOptions } from '@/shared/lib/options';
import { useSaveForm } from '@/shared/lib/useSaveForm';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import { fetchCity, fetchRegionOptions, saveCity, type City } from './api';

type Props = {
  /** Edit when set, create otherwise. */
  id?: number;
  onClose: () => void;
};

const CityForm = ({ city, onClose }: { city?: City; onClose: () => void }) => {
  const qc = useQueryClient();
  const [name, setName] = useState(city?.name ?? '');
  const [countryId, setCountryId] = useState(city?.country_id ? String(city.country_id) : '');
  const [regionId, setRegionId] = useState(city?.region_id ? String(city.region_id) : '');

  const countries = useQuery({ queryKey: ['countries-active'], queryFn: fetchActiveCountries });
  const regions = useQuery({
    queryKey: ['region-options', countryId],
    queryFn: () => fetchRegionOptions(countryId),
    enabled: Boolean(countryId),
  });

  const { errors, clearErrors, save, isPending } = useSaveForm(saveCity, () => {
    void qc.invalidateQueries({ queryKey: ['place-cities'] });
    onClose();
  });

  return (
    <ModalDialog
      onClose={onClose}
      isSaving={isPending}
      onSave={() => save({ id: city?.id, name, regionId })}
    >
      <FormField label="Страна">
        <FilterSelect
          options={toOptions(countries.data)}
          value={countryId}
          isLoading={countries.isLoading}
          onChange={(value) => {
            setCountryId(value);
            setRegionId('');
          }}
        />
      </FormField>
      <FormField label="Регион" errors={errors.region_id}>
        <FilterSelect
          options={toOptions(regions.data)}
          value={regionId}
          isDisabled={!countryId}
          isLoading={regions.isFetching}
          onChange={(value) => {
            setRegionId(value);
            clearErrors('region_id');
          }}
        />
      </FormField>
      <FormField label="Название" errors={errors.name}>
        <input
          className="form-control input-sm"
          style={{ width: 250 }}
          placeholder="Название..."
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearErrors('name');
          }}
        />
      </FormField>
      {errors.common && <p className="text-red">{errors.common[0]}</p>}
    </ModalDialog>
  );
};

export const CityModal = ({ id, onClose }: Props) => {
  const { data: city, isLoading } = useQuery({
    queryKey: ['place-city', id],
    queryFn: () => fetchCity(id as number),
    enabled: id !== undefined,
    gcTime: 0, // always edit fresh data
  });

  if (id !== undefined && isLoading) {
    return (
      <ModalDialog onClose={onClose}>
        <p>Загрузка…</p>
      </ModalDialog>
    );
  }
  return <CityForm city={city} onClose={onClose} />;
};
